import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MContentHeader } from '../../molecules/MContentHeader';
import {
  MContentHeaderAlignment,
  type MContentHeaderButton,
} from '../../molecules/MContentHeader/MContentHeader.types';
import { MTabGroup } from '../../molecules/MTabGroup';
import { MTabGroupAlignment, MTabGroupTab } from '../../molecules/MTabGroup/MTabGroup.types';
import { MButtonType } from '../../molecules/MButton/MButton.types';
import { addHashChangeListener, getHashSlug, setHashSlug } from '../../utils/urlHash';
import { toUrlSlug } from '../../utils/urlSlug';
import * as Styled from './OTabContainer.styles';
import {
  OTabContainerAlignment,
  type OTabContainerAemReactComponentMapping,
  type OTabContainerBooleanFlag,
  type OTabContainerButtonBase,
  type OTabContainerProps,
  type OTabContainerTabItem,
} from './OTabContainer.types';
import type { MSlotProps } from '../../molecules/MSlot/MSlot.types';

const COMPONENT_NAME = 'o-web-ui-kit-OTabContainer';
const TEST_ID = 'web-ui-kit-OTabContainer';
const EMPTY_TAB_ITEMS: OTabContainerTabItem[] = [];
const EMPTY_AEM_REACT_COMPONENT_MAPPING: OTabContainerAemReactComponentMapping = {};

interface OTabContainerSlotContext {
  pagePath: string;
  itemPath: string;
  isAuthorMode: boolean;
  aemReactComponentMapping: OTabContainerAemReactComponentMapping;
}

function toContentHeaderAlignment(alignment: OTabContainerAlignment): MContentHeaderAlignment {
  return alignment === OTabContainerAlignment.Centered || alignment === 'Center'
    ? MContentHeaderAlignment.Centered
    : MContentHeaderAlignment.Left;
}

function toTabGroupAlignment(alignment: OTabContainerAlignment): MTabGroupAlignment {
  return alignment === OTabContainerAlignment.Centered || alignment === 'Center'
    ? MTabGroupAlignment.Centered
    : MTabGroupAlignment.Left;
}

function toBooleanFlag(flag: OTabContainerBooleanFlag | undefined): boolean {
  return flag === true || flag === 'true';
}

function toBooleanFlagString(flag: OTabContainerBooleanFlag | undefined): 'true' | 'false' {
  return toBooleanFlag(flag) ? 'true' : 'false';
}

function toContentHeaderButton(
  button: OTabContainerButtonBase | undefined,
  variant: MButtonType.Secondary | MButtonType.Tertiary
): MContentHeaderButton | undefined {
  if (!button) return undefined;
  return {
    buttonText: button.buttonText,
    buttonType: variant,
    size: button.size,
    ctaLink: button.ctaLink,
    openInANewTab: button.openInANewTab,
  };
}

function resolveMarkedActiveIndex(items?: OTabContainerTabItem[]): number | undefined {
  if (!items || items.length === 0) return undefined;
  const found = items.findIndex(item => toBooleanFlag(item.active));
  return found >= 0 ? found : undefined;
}

function toTabHashSlug(item: OTabContainerTabItem): string {
  return toUrlSlug(item.mTabControl?.aLabel?.text);
}

function resolveHashActiveIndex(items: OTabContainerTabItem[]): number | undefined {
  if (typeof window === 'undefined' || items.length === 0) return undefined;

  const currentHash = getHashSlug({ includeSameOriginParent: true });
  if (!currentHash) return undefined;

  const requestedSlug = toUrlSlug(currentHash);
  if (!requestedSlug) return undefined;

  const found = items.findIndex(item => toTabHashSlug(item) === requestedSlug);
  return found >= 0 ? found : undefined;
}

function toTabSlot(
  item: OTabContainerTabItem,
  index: number,
  { pagePath, itemPath, isAuthorMode, aemReactComponentMapping }: OTabContainerSlotContext
): Partial<MSlotProps> | undefined {
  if (!item.mSlot && !isAuthorMode) return undefined;

  const tabItemPath = item.mSlot?.itemPath ?? (itemPath ? `${itemPath}/item${index}` : '');

  return {
    ...item.mSlot,
    pagePath: item.mSlot?.pagePath ?? pagePath,
    itemPath: tabItemPath,
    isAuthorMode: isAuthorMode || item.mSlot?.isAuthorMode,
    aemReactComponentMapping: item.mSlot?.aemReactComponentMapping ?? aemReactComponentMapping,
  };
}

function toMTabGroupTabs(items: OTabContainerTabItem[], slotContext: OTabContainerSlotContext): MTabGroupTab[] {
  return items.map((item, index) => ({
    label: item.mTabControl?.aLabel?.text ?? '',
    aIcon: item.mTabControl?.aIcon,
    description: item.mTabControl?.aLabel2?.text,
    content: item.content,
    mSlot: toTabSlot(item, index, slotContext),
  }));
}

export const OTabContainer: React.FC<OTabContainerProps> = ({
  alignment = OTabContainerAlignment.Left,
  pagePath = '',
  itemPath = '',
  isAuthorMode = false,
  aemReactComponentMapping = EMPTY_AEM_REACT_COMPONENT_MAPPING,
  enableHashDeepLink = false,
  equalTabWidths = false,
  mContentHeader,
  mTabGroup,
}) => {
  const isHashDeepLinkEnabled = toBooleanFlag(enableHashDeepLink);
  const isEqualTabWidths = toBooleanFlag(equalTabWidths);
  // `alignment` at the organism level is the only source of truth — AEM always
  // supplies it and both child blocks inherit from it. No per-block overrides.
  const contentHeaderAlignment = toContentHeaderAlignment(alignment);
  const tabGroupAlignment = toTabGroupAlignment(alignment);

  const tabItems = mTabGroup?.mTabItemMulti ?? EMPTY_TAB_ITEMS;
  const hasTabs = tabItems.length > 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasScrolledToDeepLinkedTab = useRef(false);
  // Initial active tab is derived purely from the authored `active` flag so the
  // SSR HTML and the first client render are byte-identical. The hash branch
  // runs only after hydration in the effect below — `window.location.hash` is
  // not visible to the server, so reading it during the first render would
  // create a hydration mismatch on deep-link loads.
  const [activeTabIndex, setActiveTabIndex] = useState(() => resolveMarkedActiveIndex(tabItems) ?? 0);

  const mappedTabs = useMemo(
    () => toMTabGroupTabs(tabItems, { pagePath, itemPath, isAuthorMode, aemReactComponentMapping }),
    [tabItems, pagePath, itemPath, isAuthorMode, aemReactComponentMapping]
  );
  const initialActiveIndex = useMemo(() => resolveMarkedActiveIndex(tabItems), [tabItems]);

  /*
   * Equal-width tabs: measure the natural (no-constraint) rendered width of every
   * MTabControl button, find the maximum, and write it as --o-tab-equal-width on the
   * wrapper. The CSS in OTabContainer.styles.tsx consumes it unconditionally (all
   * breakpoints) when $equalTabWidths is true.
   *
   * Measurement runs:
   *   • after every tab-set change (mappedTabs dep)
   *   • on every container resize (ResizeObserver — covers window resize, zoom,
   *     container queries, sidebar collapse, orientation change)
   *
   * FOUC prevention: `--o-tab-equal-ready: 0` is set as the initial state (via CSS)
   * which hides the tab group until the first measurement writes the width AND flips
   * the flag to `1`. This eliminates the single-frame flash of variable-width tabs.
   */
  useEffect(() => {
    if (!isEqualTabWidths || typeof window === 'undefined') return undefined;

    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    let rafId: number;

    const applyEqualWidths = () => {
      cancelAnimationFrame(rafId);
      wrapper.style.removeProperty('--o-tab-equal-width');
      rafId = requestAnimationFrame(() => {
        const tabControls = Array.from(
          wrapper.querySelectorAll<HTMLElement>('[data-component-name="m-web-ui-kit-MTabControl"]')
        );
        if (tabControls.length === 0) return;
        const maxWidth = tabControls.reduce((acc, el) => Math.max(acc, el.getBoundingClientRect().width), 0);
        if (maxWidth > 0) {
          wrapper.style.setProperty('--o-tab-equal-width', `${maxWidth}px`);
        }
        wrapper.style.setProperty('--o-tab-equal-ready', '1');
      });
    };

    applyEqualWidths();

    const ro = new ResizeObserver(applyEqualWidths);
    ro.observe(wrapper);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      wrapper.style.removeProperty('--o-tab-equal-width');
      wrapper.style.removeProperty('--o-tab-equal-ready');
    };
  }, [isEqualTabWidths, mappedTabs]);

  useEffect(() => {
    if (!isHashDeepLinkEnabled || typeof window === 'undefined') return undefined;

    const syncActiveTab = (): number | undefined => {
      const deepLinkedIndex = resolveHashActiveIndex(tabItems);
      if (deepLinkedIndex !== undefined) {
        setActiveTabIndex(deepLinkedIndex);
      }
      return deepLinkedIndex;
    };

    const initialDeepLinkedIndex = syncActiveTab();

    if (initialDeepLinkedIndex !== undefined && !hasScrolledToDeepLinkedTab.current) {
      hasScrolledToDeepLinkedTab.current = true;
      wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const handleHashChange = () => {
      const deepLinkedIndex = syncActiveTab();
      if (deepLinkedIndex !== undefined) {
        wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    return addHashChangeListener(handleHashChange, { includeSameOriginParent: true });
  }, [tabItems, isHashDeepLinkEnabled]);

  const handleTabChange = useCallback(
    (index: number) => {
      setActiveTabIndex(index);
      if (!isHashDeepLinkEnabled || typeof window === 'undefined') return;

      const selectedTab = tabItems[index];
      if (!selectedTab) return;

      const tabSlug = toTabHashSlug(selectedTab);
      if (!tabSlug) return;

      setHashSlug(tabSlug, { includeSameOriginParent: true });
    },
    [tabItems, isHashDeepLinkEnabled]
  );

  return (
    <Styled.Wrapper
      ref={wrapperRef}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      $alignment={alignment}
      $equalTabWidths={isEqualTabWidths}
    >
      {mContentHeader?.mTextBlockSimple?.aHeader?.text && (
        <MContentHeader
          alignment={contentHeaderAlignment}
          cta={toBooleanFlagString(mContentHeader.cta)}
          secondaryCta={toBooleanFlagString(mContentHeader.secondaryCta)}
          mTextBlockSimple={{
            alignment: contentHeaderAlignment,
            aEyebrow: mContentHeader.mTextBlockSimple.aEyebrow,
            aHeader: mContentHeader.mTextBlockSimple.aHeader,
            aBody: mContentHeader.mTextBlockSimple.aBody,
          }}
          mButton={toContentHeaderButton(mContentHeader.mButton, MButtonType.Tertiary)}
          mButton2={toContentHeaderButton(mContentHeader.mButton2, MButtonType.Secondary)}
        />
      )}
      {hasTabs && (
        <MTabGroup
          alignment={tabGroupAlignment}
          icon={toBooleanFlag(mTabGroup?.icon)}
          description={toBooleanFlag(mTabGroup?.description)}
          tabs={mappedTabs}
          activeIndex={activeTabIndex}
          initialActiveIndex={initialActiveIndex}
          onTabChange={handleTabChange}
        />
      )}
    </Styled.Wrapper>
  );
};

export { TEST_ID as O_TAB_CONTAINER_TEST_ID, COMPONENT_NAME as O_TAB_CONTAINER_COMPONENT_NAME };
